import { useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "components/primitives/Grid";
import { useUnitFormatter } from "hooks";
import moment from "moment";
import useMetadataActions from "providers/MetadataProvider/useMetadataActions";
import { useNavigate } from "react-router-dom";
import ButtonBase from "@mui/material/ButtonBase";
import logEvent from "utils/logEvent";
import AnalyticsService from "services/AnalyticsService";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import MetadataHelpers from "helpers/MetadataHelpers";
import { useTranslation } from "react-i18next";
import LiveActivityFeedVirtualTableRowCell from "./LiveActivityFeedVirtualTableRowCell";
import type { FireStoreMeasurement, Variants } from "types";
import { MetadataType } from "types";
import MetadataNameCell from "./MetadataNameCell";
import TimeHelper from "helpers/TimeHelper";

function LiveActivityFeedVirtualTableRowData(props: FireStoreMeasurement) {
  const { exerciseId, completedDate, athleteId, id: measurementId } = props;
  const metadataActions = useMetadataActions();

  const { metadata, defaultMetricMetadata, defaultMetricValue, repMetric, weightMetric } = useMemo(() => {
    const metadata = metadataActions.getById(exerciseId);
    const exercise = metadata?.type === MetadataType.EXERCISE ? metadata.metadata : undefined;
    const defaultMetricMetadata = MetadataHelpers.getMetadataMetricByField(metadata, null);
    const defaultMetricValue = defaultMetricMetadata ? props[defaultMetricMetadata.field] : undefined;
    const repMetric: number | undefined = props["repCount"];
    const weightMetric = ExerciseHelpers.getWeightMetric(exercise);

    return { metadata, defaultMetricMetadata, defaultMetricValue, repMetric, weightMetric };
  }, [metadataActions, exerciseId, props]);

  const formatUnit = useUnitFormatter();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const time = moment(completedDate);

  const onClick = useCallback(() => {
    logEvent("HUB_LIVE_ATHLETE_PROFILE_CHART_EXERCISE", {
      [AnalyticsService.PROPERTIES.EXERCISE_ID]: exerciseId,
      [AnalyticsService.PROPERTIES.EVENT_CLICK_LOCATION]: "liveActivityFeed",
    });
    navigate(`/athlete-hub/${athleteId}?chartType=LINE&metadataId=${exerciseId}`);
  }, [exerciseId, navigate, athleteId]);

  const timeLabel = useMemo(() => {
    const customCalendarFormat = {
      sameDay: `[${t("global.today_label")}]`,
      lastDay: `[${t("global.yesterday_label")}]`,
      lastWeek: "MMM DD",
      sameElse: "MMM DD",
    };
    return TimeHelper.formatCalendarTime(time, customCalendarFormat);
  }, [t, time]);

  return (
    <Box component={ButtonBase} onClick={onClick} sx={{ width: "100%" }}>
      <Box sx={{ minHeight: "48px", width: "100%", display: "flex", alignItems: "center" }}>
        <Grid container columnSpacing="16px" sx={{ width: "100%" }}>
          <Grid title={time.toLocaleString()}>
            <LiveActivityFeedVirtualTableRowCell label={timeLabel} value={time.format("HH:mm")} weight="500" />
          </Grid>
          <Grid size={3}>
            <MetadataNameCell
              athleteId={athleteId}
              exerciseId={exerciseId}
              metadata={metadata}
              variant={(props.variant as Variants) ?? null}
              defaultMetricValue={defaultMetricValue}
              completedDate={completedDate}
              measurementId={measurementId}
            />
          </Grid>
          <Grid container size={4} columnSpacing="16px">
            {!!repMetric && (
              <Grid>
                <LiveActivityFeedVirtualTableRowCell label={t("global.reps_label_text")} value={`${repMetric}`} />
              </Grid>
            )}
            {!!props.weight && (
              <Grid>
                <LiveActivityFeedVirtualTableRowCell
                  label={t("global.weight_label_text")}
                  value={formatUnit(props.weight, weightMetric)}
                />
              </Grid>
            )}
            {!!props.variant && (
              <Grid>
                <LiveActivityFeedVirtualTableRowCell
                  label={t("global.variant_label_text")}
                  value={t("measures.variant", { context: props.variant })}
                />
              </Grid>
            )}
          </Grid>
          <Grid size={3}>
            {defaultMetricMetadata &&
              defaultMetricMetadata.field !== "weight" &&
              typeof defaultMetricValue === "number" &&
              !isNaN(defaultMetricValue) && (
                <LiveActivityFeedVirtualTableRowCell
                  label={defaultMetricMetadata.name}
                  value={formatUnit(defaultMetricValue, defaultMetricMetadata)}
                />
              )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default LiveActivityFeedVirtualTableRowData;
